import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { runGqlMutation } from "gqlClient";
import { EnqueueGenerateProfileResponsesDocument, type SearchCasesResult } from "generated/graphql";
import { empty, fold } from "helpers/coll";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectFormFields } from "redux/slices/form.slice";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import CaseAutocomplete from "components/CaseAutocomplete";
import { P } from "theme/typography";
import { usePubNub } from "pubnub-react";
import { MessageEvent } from "pubnub";
import theme, { EllipsisSX } from "theme";
import { Close, CloseTwoTone, FiberManualRecord, FileUpload } from "@mui/icons-material";
import {
  addProfgenBatch,
  addProfgenSampleEvent,
  addProfgenStatusEvent,
  selectLensEditorFieldAiConfigs,
  selectProfgenBatches,
} from "redux/slices/lensEditor";
import PSelect from "components/PSelect";
import MenuItem from "@mui/material/MenuItem";
import { MultipleFormFieldSelect } from "components/FormFieldSelect";
import LoadingSpinner from "components/LoadingSpinner";
import HelpIcon from "@mui/icons-material/Help";
import {
  GenerateProfileResponseSampleEvent,
  GenerateProfileResponseStatusEvent,
} from "routes/lens-editor/EventInterfaces";
import PasteCasesDialog from "../PasteCasesDialog";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { extractGQLError } from "shared/PatternGraphQLError";
import { selectClient } from "redux/slices/session";
import SampleEventTable from "./SampleEventTable";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import LensPatchDisplay from "./LensPatchDisplay";

type FormState = {
  fieldIds: number[];
  allCases: boolean;
  cases: SearchCasesResult[];
  caseResponseQuota: number | undefined;
  sampleOnly: boolean;
  parallelism: number | undefined;
  maximumPageSets: number | undefined;
  consolidate: boolean;
  priority: boolean;
  allDocs: boolean;
  wipeOnStart: boolean;
  lensPatchForRuleFilters: string | undefined;
};

const initialValues: FormState = {
  fieldIds: [],
  allCases: false,
  cases: [],
  caseResponseQuota: undefined,
  sampleOnly: true,
  parallelism: undefined,
  maximumPageSets: undefined,
  consolidate: false,
  priority: false,
  allDocs: false,
  wipeOnStart: false,
  lensPatchForRuleFilters: undefined,
};
function isProfileSampleEvent(evt: any): evt is GenerateProfileResponseSampleEvent {
  return evt.type === "GenerateProfileResponseSampleEvent" && evt.batchId !== undefined && evt.outcome !== undefined;
}
function isProfileStatusEvent(evt: any): evt is GenerateProfileResponseStatusEvent {
  return evt.type === "GenerateProfileResponseStatusEvent" && evt.batchId !== undefined && evt.outcome !== undefined;
}

const ProfileGenerationRoute = () => {
  const pubnub = usePubNub();
  const lensId = useParamNumbers(["lensId"]).lensId!;
  const clientName = useAppSelector(selectClient)?.name;
  const dispatch = useAppDispatch();
  const lensPatchFileRef = useRef<HTMLInputElement>(null);
  const fields = useAppSelector(selectFormFields);
  const fieldAiConfigs = useAppSelector(selectLensEditorFieldAiConfigs);
  const { enqueueSnackbar } = useSnackbar();
  const [enqueueLoading, setEnqueueLoading] = useState(false);
  const [batchId, setBatchId] = useState<string | undefined>();
  const [pastedCaseIds, setPastedCaseIds] = useState<number[]>([]);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const profgenBatches = useAppSelector(selectProfgenBatches);
  const batchIds = Object.keys(profgenBatches);
  const [selectedCaseId, setSelectedCaseId] = useState<number | undefined>(undefined);
  const selectedBatchState = batchId ? profgenBatches[batchId] : {};
  const selectedBatchEntries = useMemo(() => Object.entries(selectedBatchState ?? {}), [selectedBatchState]);
  const selectedBatchAggregateStatus: [number, number] = useMemo(
    () =>
      fold(selectedBatchEntries, [0, 0], ([complete, total], [_key, value]) => {
        const fieldEntries = Object.entries(value.fieldStates);
        const thisComplete = fieldEntries.filter((fs) => fs[1].status === "end").length;
        const thisTotal = fieldEntries.length;
        return [complete + thisComplete, total + thisTotal];
      }),
    [selectedBatchEntries]
  );
  const selectedBatchEntriesFiltered = selectedBatchEntries.filter(
    (ent) => selectedCaseId === undefined || ent[0] === selectedCaseId.toString()
  );
  const selectedBatchCaseIds = Array.from(selectedBatchEntries.map((e) => parseInt(e[0])));
  const sampleEventListener = useMemo(
    () => ({
      message: (evt: MessageEvent) => {
        const payload = JSON.parse(evt.message);
        if (isProfileSampleEvent(payload)) {
          dispatch(addProfgenSampleEvent({ event: payload }));
        }
        if (isProfileStatusEvent(payload)) {
          dispatch(addProfgenStatusEvent({ event: payload }));
        }
      },
    }),
    [dispatch]
  );
  useEffect(() => {
    if (batchId) {
      pubnub.addListener(sampleEventListener);
      pubnub.subscribe({ channels: [`genprofilesample_${batchId}`] });

      return () => {
        pubnub.removeListener(sampleEventListener);
        pubnub.unsubscribe({ channels: [`genprofilesample_${batchId}`] });
      };
    }
  }, [pubnub, batchId]);
  const confirmCtx = useContext(ConfirmationDialogContext);

  const formik = useFormik<FormState>({
    initialValues,
    validateOnMount: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.fieldIds.length === 0) errors.fieldIds = "Required";
      if (!values.allCases && empty(values.cases) && empty(pastedCaseIds)) {
        errors.cases = "Must provide cases or check 'All Cases'";
      }
      return errors;
    },
    onSubmit: (values) => {
      if (values.allCases) {
        confirmCtx.confirm({
          title: "Run on all cases?",
          confirmButtonText: "Run",
          prompt: <P variant="sm">Are you sure you want to run on all cases?</P>,
          callback: () => runMutation(values),
        });
      } else if (values.wipeOnStart) {
        confirmCtx.confirm({
          title: "Run with Clear Existing Responses?",
          confirmButtonText: "DELETE and Run",
          prompt: (
            <>
              <P variant="sm">
                This will clear all existing profile responses for the fields and cells you have selected, as soon as
                the run is submitted.
              </P>
              <P />
              <P variant="h5">Client: {clientName ?? "{unknown}"}</P>
            </>
          ),
          callback: () => runMutation(values),
        });
      } else {
        runMutation(values);
      }
    },
  });

  const handleLensPatchUploadClick = () => {
    if (lensPatchFileRef.current) lensPatchFileRef.current.click();
  };

  const handleLensPatchFile = (evt: ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = function (evt) {
      formik.setFieldValue("lensPatchForRuleFilters", evt.target?.result as string);
      if (lensPatchFileRef.current) {
        lensPatchFileRef.current.value = "";
      }
    };
  };

  function runMutation(values: FormState = formik.values): Promise<any> {
    setEnqueueLoading(true);

    return runGqlMutation(EnqueueGenerateProfileResponsesDocument, {
      lensId: lensId,
      fieldIds: values.fieldIds,
      allCases: values.allCases,
      caseIds: [...pastedCaseIds, ...values.cases.map((c) => c.caseId)],
      caseResponseQuota: values.caseResponseQuota,
      parallelism: values.parallelism,
      maximumPageSets: values.maximumPageSets,
      sampleOnly: values.sampleOnly,
      consolidate: values.consolidate,
      priority: values.priority,
      allDocuments: values.allDocs,
      wipeOnStart: values.wipeOnStart,
      lensPatchForRuleFilters: values.lensPatchForRuleFilters,
    })
      .then((response) => {
        const { batchId, caseIds } = response.lens.aiChat.enqueueGenerateProfileResponses;
        enqueueSnackbar(`profgen batch created with ID: ${batchId}`, { variant: "success" });
        setBatchId(batchId);
        dispatch(addProfgenBatch({ batchId, caseIds, fieldIds: values.fieldIds }));
        setEnqueueLoading(false);
      })
      .catch((err) => {
        const error = extractGQLError(err);
        setEnqueueLoading(false);
        enqueueSnackbar(`Call failed: ${error?.message}`, {
          variant: "error",
        });
      });
  }

  const handleAddCase = (ccase: SearchCasesResult | undefined) => {
    if (ccase) {
      formik.setFieldValue("cases", [...formik.values.cases, ccase], true);
    }
  };

  const hasFile = formik.values.lensPatchForRuleFilters !== undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "row", p: 1, flex: 1, height: "100%" }}>
      <form onSubmit={formik.handleSubmit}>
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: "200px",
            height: 1,
            p: 2,
            flex: 0,
            gap: 1,
            maxWidth: 300,
          }}>
          <MultipleFormFieldSelect
            fieldIds={formik.values.fieldIds}
            label="Field"
            changeFieldIds={(fieldIds) => formik.setFieldValue("fieldIds", fieldIds, true)}
            filterOption={(field) => field.repeating && !!fieldAiConfigs.find((c) => c.fieldId === field.id)}
          />
          <CaseAutocomplete
            label="Cases"
            lensId={lensId}
            clearOnChange
            onChange={(selectedCase) => handleAddCase(selectedCase)}
          />
          <Button variant="text" onClick={() => setPasteDialogOpen(true)}>
            Paste Pattern Case Ids
          </Button>

          <PasteCasesDialog
            open={pasteDialogOpen}
            onConfirm={(caseIds) => {
              setPasteDialogOpen(false);
              setPastedCaseIds(caseIds);
              // force formik to revalidate
              formik.setFieldValue("cases", formik.values.cases, true);
            }}
            onCancel={() => {
              setPasteDialogOpen(false);
            }}
          />

          <FormControlLabel
            label="All Cases"
            name="allCases"
            control={<Checkbox checked={formik.values.allCases} onChange={formik.handleChange} />}
          />

          <FormControlLabel
            label={
              <span>
                Sample Only{" "}
                <Tooltip title="See which pages and how many will be ran">
                  <HelpIcon sx={{ fontSize: 12, ml: 1 }} />
                </Tooltip>
              </span>
            }
            name="sampleOnly"
            control={<Checkbox checked={formik.values.sampleOnly} onChange={formik.handleChange} />}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              label="Case Response Quota"
              name="caseResponseQuota"
              type="number"
              value={formik.values.caseResponseQuota}
              error={!!formik.errors.caseResponseQuota}
              onChange={formik.handleChange}
            />
            <Tooltip title="The maximum number of Profile Responses that will be generated per case and field by this run. Does not take existing responses into account">
              <HelpIcon sx={{ fontSize: 12, ml: 1 }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              label="Maximum Page Sets"
              name="maximumPageSets"
              type="number"
              value={formik.values.maximumPageSets}
              error={!!formik.errors.maximumPageSets}
              onChange={(evt) =>
                formik.setFieldValue(
                  "maximumPageSets",
                  evt.target.value === "" ? undefined : parseInt(evt.target.value)
                )
              }
            />
            <Tooltip title="The maximum number of page sets that will be considered per case and field. Random sampling is used to enforce this maximum. This is a statistical target; not a hard maximum.">
              <HelpIcon sx={{ fontSize: 12, ml: 1 }} />
            </Tooltip>
          </Box>
          <FormControlLabel
            label={
              <span>
                Consolidate
                <Tooltip title="Consolidate generated profile responses with existing">
                  <HelpIcon sx={{ fontSize: 12, ml: 1 }} />
                </Tooltip>
              </span>
            }
            name="consolidate"
            control={<Checkbox checked={formik.values.consolidate} onChange={formik.handleChange} />}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              label="Parallelism (1-4)"
              name="parallelism"
              type="number"
              value={formik.values.parallelism}
              error={!!formik.errors.parallelism}
              onChange={(evt) =>
                formik.setFieldValue("parallelism", evt.target.value === "" ? undefined : parseInt(evt.target.value))
              }
            />
            <Tooltip title="Number of concurrent detection requests per case and field; 1-4">
              <HelpIcon sx={{ fontSize: 12, ml: 1 }} />
            </Tooltip>
          </Box>
          <FormControlLabel
            label={
              <span>
                Priority
                <Tooltip title="Place jobs for this run in the priority scheduled events queue, rather than the standard scheduled events queue">
                  <HelpIcon sx={{ ml: 1, fontSize: 12 }} />
                </Tooltip>
              </span>
            }
            name="priority"
            control={<Checkbox checked={formik.values.priority} onChange={formik.handleChange} />}
          />
          <FormControlLabel
            label={
              <span>
                All Docs
                <Tooltip title="Consider all docs for a case, not just new ones">
                  <HelpIcon sx={{ ml: 1, fontSize: 12 }} />
                </Tooltip>
              </span>
            }
            name="allDocs"
            control={<Checkbox checked={formik.values.allDocs} onChange={formik.handleChange} />}
          />
          <FormControlLabel
            label={
              <span>
                Clear Existing Responses
                <Tooltip title="Deletes ALL EXISTING RESPONSES for the given cases and fields">
                  <HelpIcon sx={{ ml: 1, fontSize: 12 }} />
                </Tooltip>
              </span>
            }
            name="wipeOnStart"
            control={<Checkbox checked={formik.values.wipeOnStart} onChange={formik.handleChange} />}
          />
          <div className="grid">
            <div className="flex items-center">
              <p className="align-mitext-sm w-[200px]">{hasFile ? "Patch selected" : "Choose Lens Patch Filter"}</p>
              {hasFile ? (
                formik.values.lensPatchForRuleFilters && (
                  <IconButton size="small" onClick={() => formik.setFieldValue("lensPatchForRuleFilters", undefined)}>
                    <CloseTwoTone />
                  </IconButton>
                )
              ) : (
                <IconButton size="small" onClick={handleLensPatchUploadClick}>
                  <FileUpload />
                  <input
                    ref={lensPatchFileRef}
                    type="file"
                    autoComplete="off"
                    tabIndex={-1}
                    style={{ display: "none" }}
                    onChange={handleLensPatchFile}
                  />
                </IconButton>
              )}
            </div>
            {hasFile && formik.values.lensPatchForRuleFilters && (
              <LensPatchDisplay patch={formik.values.lensPatchForRuleFilters} />
            )}
          </div>

          <Box sx={{ overflow: "auto", display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
            {pastedCaseIds.length > 0 && (
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <P variant="sm" sx={{ color: "white", ...EllipsisSX }}>
                  {pastedCaseIds.length} cases pasted
                </P>
                <IconButton
                  size="small"
                  sx={{ color: "white" }}
                  onClick={() => {
                    setPastedCaseIds([]);
                  }}>
                  <Close />
                </IconButton>
              </Box>
            )}

            {formik.values.cases.map((c) => {
              return (
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    backgroundColor: theme.palette.grey[400],
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  key={c.caseId}>
                  <P variant="sm" sx={{ color: "white", ...EllipsisSX }}>
                    {c.claimantName}
                  </P>
                  <IconButton
                    size="small"
                    sx={{ color: "white" }}
                    onClick={() => {
                      formik.setFieldValue(
                        "cases",
                        formik.values.cases.filter((x) => x.caseId !== c.caseId),
                        true
                      );
                    }}>
                    <Close />
                  </IconButton>
                </Box>
              );
            })}
          </Box>

          <Button color="primary" variant="contained" type="submit" disabled={!formik.isValid || enqueueLoading}>
            Submit
          </Button>
        </Paper>
      </form>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ pl: 2, display: "flex", flexDirection: "row", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Batch Id</InputLabel>
            <PSelect label="Batch Id" value={batchId || ""} onChange={(evt) => setBatchId(evt.target.value as string)}>
              {batchIds.map((batchId) => (
                <MenuItem key={batchId} value={batchId}>
                  {batchId}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
          {batchId && (
            <>
              <P sx={{ ml: 1 }} variant="xsBold">
                {Object.keys(selectedBatchState ?? {}).length} cases
              </P>
              <P sx={{ ml: 3 }}>Case Filter: </P>
              <PSelect
                value={selectedCaseId || ""}
                onChange={(evt) => {
                  const value = evt.target.value as number;
                  setSelectedCaseId(value === -1 ? undefined : value);
                }}>
                <MenuItem value={-1}>None</MenuItem>
                {selectedBatchCaseIds.map((caseId) => (
                  <MenuItem key={caseId} value={caseId}>
                    {caseId}
                  </MenuItem>
                ))}
              </PSelect>
              <Box sx={{ display: "flex", flexDirection: "column", ml: 2 }}>
                <P variant="xsBold">
                  {selectedBatchAggregateStatus[0]} / {selectedBatchAggregateStatus[1]} Case+Fields Complete
                </P>
                <LinearProgress
                  variant="determinate"
                  sx={{ width: 200 }}
                  value={(selectedBatchAggregateStatus[0] / selectedBatchAggregateStatus[1]) * 100}
                />
              </Box>
            </>
          )}
        </Box>

        {selectedBatchEntriesFiltered.slice(0, 1000).map(([caseId, state]) => {
          return (
            <Paper key={caseId} sx={{ m: 1, p: 1 }}>
              <P variant="lg">
                {state.clientCaseId} (case id: {caseId})
              </P>
              <Box sx={{ display: "flex", flexDirection: "column", overflow: "auto", maxHeight: "500px" }}>
                {Object.entries(state.fieldStates).map(([fieldId, fieldState]) => {
                  const field = fields.find((f) => f.id.toString() === fieldId);
                  return (
                    <Box key={fieldId} sx={{ p: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {fieldState.status !== "end" ? (
                          <LoadingSpinner size={24} />
                        ) : (
                          <FiberManualRecord htmlColor={theme.palette.success.main} />
                        )}
                        <P sx={{ ml: 1 }}>
                          {field?.outlinePrefix} {field?.name}
                        </P>
                      </Box>
                      <P variant="caption">
                        Status: <b>{fieldState.status}</b> {fieldState.events.length} Samples
                      </P>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <SampleEventTable lensId={lensId} sampleEvents={fieldState.events} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProfileGenerationRoute;
